import { useEffect, useRef, useState } from 'react';

import { ensureOrbitalMap, orbitalMapRegistered } from './vendor.js';
import { adopt } from '../../../vendor/maps/scenario.js';
import type { SpaceLayers } from '../model/map-layers.js';
import type { ScenarioModel } from '../model/scenario.js';
import type { MapCommand } from '../state/store.js';
import type { MapContextKind, MapModifierClick } from './RegionMap.js';

/**
 * The typed React wrapper over `vendor/maps/orbital-map.js` (M5-D7) — the third,
 * and the shape `maps/RegionMap.tsx` set.
 *
 * Props in → `api` calls; `CustomEvent`s out → callbacks. The imperative surface,
 * from the renderer's own `boot()` at line 203:
 *
 * ```
 * select · focusUnit · focusRegime · setLayers · setDim · setPick
 * setCrossLink · setHover · fitSide · resetView · theme
 * ```
 *
 * and out: `bs-map-ready`, `bs-select`, `bs-region`, `bs-pick`, `bs-context`,
 * `bs-hover`, `bs-mod-click`.
 *
 * ## Four differences from the theatre wrapper, and each changes what is built
 *
 * **`fitSide` is `() => {}`** — an empty function in the vendored file, not a
 * missing one. So FIT TO FRIENDLY FORCES and FIT TO THREATS are not offered on
 * this canvas, which is also what the design does (its SDA canvas menu has the
 * two camera actions and the layer switches and no fit pair). A menu item wired
 * to an empty function is a control that does nothing.
 *
 * **There is no `bs-engage`**, so the drag-an-asset-onto-a-contact gesture the
 * theatre wired does not exist here. An SDA seat reaches the builder through the
 * context menu, ⇧/⌥-click and the pick slots, all of which do.
 *
 * **There is no `setTerrain`** — no basemap, no third-party tile service, and
 * therefore no degradation to state (Q23 does not reach this board).
 *
 * **A snapshot needs no `geometry()`.** The theatre's two off-`api` calls
 * (Phase 5's finding 3) exist because `region-map.js` solves each region's slot
 * set once; this renderer solves nothing — a marker's position is `slotAngle` of
 * the slot the wire named. What it *does* cache is the merge pass, keyed on the
 * camera and the layers and not on the roster, so a new snapshot is pushed with
 * `api.setLayers({})`: an empty merge into `state.layers`, then `invalidate()`
 * and `render()`. That is on `api`, it is what the design's own bridge uses for
 * the same purpose, and it means nothing here reaches past the published surface.
 *
 * ## Keys this file does not bind
 *
 * `v`, `r`, `g`, `[` and `]` are the vendored element's own, bound on `document`
 * and guarded on `isConnected`, on `INPUT`/`TEXTAREA` and on modifiers. None of
 * them collides with the shell's nine, so the design's SDA help block is
 * implemented by the renderer and `model/hotkeys.ts` owes it four rows of text
 * rather than four handlers. The listener is removed in `disconnectedCallback`,
 * which is why this wrapper removes the element rather than hiding it: an element
 * left mounted under a hidden tab would answer `v` and `r` from another board.
 *
 * `Escape` is the one overlap, and it is not new — `region-map.js` binds it too.
 * The renderer clears its own selection and emits `bs-select null` *followed by*
 * `bs-region null`, so the store stays in step only if both are honoured; see
 * `echoed` below, which is Phase 5's guard read carefully rather than reused
 * blind, because on this canvas the two arrive as an ordered pair.
 */

/** The subset of the vendored element's surface this wrapper drives. */
interface OrbitalMapElement extends HTMLElement {
  api?: {
    select?: (id: string | null) => void;
    focusUnit?: (id: string) => void;
    focusRegime?: (band: string) => void;
    setLayers?: (layers: Partial<SpaceLayers>) => void;
    setDim?: (ids: readonly string[] | null) => void;
    setPick?: (pick: { slot: string; ids: readonly string[] } | null) => void;
    setCrossLink?: (info: { spaceId: string; groundLabel: string } | null) => void;
    setHover?: (id: string | null) => void;
    resetView?: () => void;
  };
}

export interface OrbitalMapProps {
  readonly model: ScenarioModel;
  readonly layers: SpaceLayers;
  /** The selected asset or contact. A selected *band* is not a unit selection. */
  readonly selectedUnit: string | null;
  /** Ids matching the roster filter, or `null` for no filter. Dimmed, never hidden. */
  readonly dim: readonly string[] | null;
  /** The open task slot's candidates — a click on one fills the slot. */
  readonly pick: { readonly slot: string; readonly ids: readonly string[] } | null;
  readonly hover: string | null;
  /** The selected satellite and the station that flies it (`model/orbital.ts`). */
  readonly crossLink: { readonly spaceId: string; readonly groundLabel: string } | null;
  /** Reveal or reset — whatever the shell has asked the canvas for. */
  readonly command: MapCommand | null;

  readonly onSelect: (id: string | null) => void;
  readonly onBand: (band: string | null) => void;
  readonly onPick: (slot: string, id: string) => void;
  readonly onContext: (kind: MapContextKind, id: string, x: number, y: number) => void;
  readonly onHover: (id: string | null) => void;
  readonly onModClick: (detail: MapModifierClick) => void;
  readonly onCommandApplied: (seq: number) => void;
}

const TAG = 'orbital-map';

export function OrbitalMap(props: OrbitalMapProps): JSX.Element {
  const hostRef = useRef<HTMLDivElement>(null);
  const elementRef = useRef<OrbitalMapElement | null>(null);
  const [registered, setRegistered] = useState(orbitalMapRegistered);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let live = true;
    void ensureOrbitalMap().then(() => {
      if (live) setRegistered(orbitalMapRegistered());
    });
    return () => {
      live = false;
    };
  }, []);

  /**
   * The last id the *element* reported selecting.
   *
   * Two jobs, and the second is this canvas's own. It stops the console fighting
   * itself over a push — `api.select(null)` in answer to a band selection would
   * clear the ring the operator just picked. And it decides which `bs-region` to
   * take: `focusUnit` and `focusRegime` both report a regime **while a unit is
   * still selected**, and taking that would swap the operator's subject for the
   * ring under it (Phase 5's finding 4).
   *
   * The order matters here in a way it did not on the theatre map.
   * `clearSelection` and `selectRegime` emit `bs-select` *first* and `bs-region`
   * second, so by the time the second arrives this ref is already `null` and the
   * band change is taken. A guard that read a stale selection would eat it.
   */
  const echoed = useRef<string | null>(null);

  // Callbacks are read through a ref so that a parent re-rendering with new
  // closures does not tear the element down and rebuild it, which would throw
  // away the operator's camera on every snapshot.
  const handlers = useRef(props);
  handlers.current = props;

  const { model } = props;
  const initial = useRef({ model });

  useEffect(() => {
    const host = hostRef.current;
    if (host === null || !registered) return;

    // Seeded before the element is connected: `connectedCallback` reads the
    // adopted model in `boot()`.
    adopt(initial.current.model);

    const element = document.createElement(TAG) as OrbitalMapElement;
    element.style.width = '100%';
    element.style.height = '100%';
    element.setAttribute('data-testid', 'orbital-map-canvas');
    element.setAttribute('role', 'group');
    element.setAttribute('aria-label', 'Orbital board');

    /*
      The canvas is named from out here, because it cannot be named from inside.

      `<orbital-map>` arrived with no `role`, no name and no `<title>`, and sixty
      -five of its nodes carry `cursor: pointer` while none carries a `tabindex` —
      so the page's namesake element did not exist for a screen reader and could
      not be entered from the keyboard. The renderer is vendored (M5-D7) and a
      patch in it is a re-vendor conflict, so the wrapper does the naming: it owns
      the host, and the ready event is the first moment the subtree exists.

      `role="img"` on the `<svg>` is the honest description rather than a
      concession. Every marker on it is a mouse target with no keyboard
      equivalent, and the roster rail is already a complete, keyboard-navigable
      list of every object drawn here — so the picture is a picture, and the rail
      is the path. Announcing sixty-five unreachable "buttons" would be worse than
      announcing one image. The renderer's own five controls sit in sibling
      divs rather than inside the `<svg>`, so they keep their own semantics; what
      they lacked was names anyone could act on.
    */
    const onReady = (): void => {
      setReady(true);
      const svg = element.querySelector('svg');
      if (svg !== null) {
        svg.setAttribute('role', 'img');
        svg.setAttribute('aria-label', 'Orbital board — assets on the LEO, MEO, GEO and HEO rings');
        svg.setAttribute('focusable', 'false');
      }
      nameControls();
    };

    const CONTROL_NAMES: readonly (readonly [string, string])[] = [
      ['+', 'Zoom in'],
      ['−', 'Zoom out'],
      ['RST', 'Reset view'],
      ['2D', 'Switch between 2D and 3D'],
      ['3D', 'Switch between 2D and 3D'],
      ['LEGEND', 'Legend'],
    ];

    const nameControls = (): void => {
      element.querySelectorAll('button').forEach((button) => {
        const text = (button.textContent ?? '').trim();
        const hit = CONTROL_NAMES.find(([prefix]) => text.startsWith(prefix));
        if (hit !== undefined) button.setAttribute('aria-label', hit[1]);
      });
    };

    /*
      The names have to be reapplied, because the renderer rebuilds the controls.

      Two things put them back the way they were: the LEGEND pill rewrites its own
      `textContent` when it opens, and a theme change rebuilds the whole control
      column — the renderer watches `data-theme` on `<html>` itself, so there is no
      prop and no effect on this side to hang the reapply on. Labelling once on
      ready therefore held until the operator pressed L, and measured dark-theme
      runs came back with LEGEND unnamed.

      An observer rather than a poll, coalesced to one frame because the renderer
      re-renders its `<svg>` on every pan and zoom. The work per pass is a query
      over five buttons; the guard is there so it is five buttons per frame rather
      than five per mutation record.
    */
    let queued = 0;
    const observer = new MutationObserver(() => {
      if (queued !== 0) return;
      queued = requestAnimationFrame(() => {
        queued = 0;
        nameControls();
      });
    });
    observer.observe(element, { childList: true, subtree: true, characterData: true });
    const onSelect = (event: Event): void => {
      const id = (event as CustomEvent<string | null>).detail ?? null;
      echoed.current = id;
      handlers.current.onSelect(id);
    };
    const onRegion = (event: Event): void => {
      if (echoed.current !== null) return;
      handlers.current.onBand((event as CustomEvent<string | null>).detail ?? null);
    };
    const onPick = (event: Event): void => {
      // `slot` is the *task* slot the shell armed, not an orbital one: the
      // renderer echoes back whatever `setPick` handed it. Nothing on this
      // canvas reports a slot number, so nothing has to be mapped home.
      const detail = (event as CustomEvent<{ slot?: string; id?: string }>).detail;
      if (detail?.slot !== undefined && detail.id !== undefined) {
        handlers.current.onPick(detail.slot, detail.id);
      }
    };
    const onContext = (event: Event): void => {
      const detail = (
        event as CustomEvent<{
          kind: MapContextKind;
          id: string | null;
          x: number;
          y: number;
        }>
      ).detail;
      handlers.current.onContext(detail.kind, detail.id ?? '', detail.x, detail.y);
    };
    const onHover = (event: Event): void => {
      handlers.current.onHover((event as CustomEvent<string | null>).detail ?? null);
    };
    const onModClick = (event: Event): void => {
      handlers.current.onModClick((event as CustomEvent<MapModifierClick>).detail);
    };

    element.addEventListener('bs-map-ready', onReady);
    element.addEventListener('bs-select', onSelect);
    element.addEventListener('bs-region', onRegion);
    element.addEventListener('bs-pick', onPick);
    element.addEventListener('bs-context', onContext);
    element.addEventListener('bs-hover', onHover);
    element.addEventListener('bs-mod-click', onModClick);

    host.appendChild(element);
    elementRef.current = element;
    return () => {
      observer.disconnect();
      if (queued !== 0) cancelAnimationFrame(queued);
      element.removeEventListener('bs-map-ready', onReady);
      element.removeEventListener('bs-select', onSelect);
      element.removeEventListener('bs-region', onRegion);
      element.removeEventListener('bs-pick', onPick);
      element.removeEventListener('bs-context', onContext);
      element.removeEventListener('bs-hover', onHover);
      element.removeEventListener('bs-mod-click', onModClick);
      // Removed rather than hidden: `disconnectedCallback` is what takes the
      // renderer's five keys off `document`.
      element.remove();
      elementRef.current = null;
      setReady(false);
    };
  }, [registered]);

  // A new model is a new snapshot. Adopting changes what the next draw reads and
  // `setLayers({})` is what makes the next draw happen — see the header.
  useEffect(() => {
    adopt(model);
    if (ready) elementRef.current?.api?.setLayers?.({});
  }, [model, ready]);

  const layers = props.layers;
  useEffect(() => {
    if (ready) elementRef.current?.api?.setLayers?.(layers);
  }, [layers, ready]);

  const selectedUnit = props.selectedUnit;
  useEffect(() => {
    if (!ready || selectedUnit === echoed.current) return;
    echoed.current = selectedUnit;
    elementRef.current?.api?.select?.(selectedUnit);
  }, [selectedUnit, ready]);

  const dim = props.dim;
  useEffect(() => {
    if (ready) elementRef.current?.api?.setDim?.(dim);
  }, [dim, ready]);

  const pick = props.pick;
  useEffect(() => {
    if (ready) elementRef.current?.api?.setPick?.(pick);
  }, [pick, ready]);

  const hover = props.hover;
  useEffect(() => {
    if (ready) elementRef.current?.api?.setHover?.(hover);
  }, [hover, ready]);

  const crossLink = props.crossLink;
  useEffect(() => {
    if (ready) elementRef.current?.api?.setCrossLink?.(crossLink);
  }, [crossLink, ready]);

  const command = props.command;
  const onCommandApplied = props.onCommandApplied;
  useEffect(() => {
    if (!ready || command === null) return;
    const api = elementRef.current?.api;
    switch (command.kind) {
      case 'UNIT':
        api?.focusUnit?.(command.id);
        break;
      case 'BAND':
        api?.focusRegime?.(command.id);
        break;
      case 'RESET':
        api?.resetView?.();
        break;
      // `REGION` is a zone on the ground and `FIT` is `() => {}` here — neither
      // is offered on this board, and neither is silently swallowed elsewhere.
      case 'REGION':
      case 'FIT':
        break;
    }
    onCommandApplied(command.seq);
  }, [command, ready, onCommandApplied]);

  return (
    <div
      ref={hostRef}
      data-testid="orbital-map"
      data-ready={ready ? 'true' : 'false'}
      style={{ flex: 1, minWidth: 0, minHeight: 0, position: 'relative' }}
    />
  );
}
